import { TitleProps } from "antd/lib/typography/Title"
import React from "react"
import DOMPurify from "dompurify"
import TextTransition from "react-text-transition"

import styles from "./Title.module.scss"

const style = (type: string): string => {
  switch (type) {
    case "error":
      return styles.Error
    case "information":
      return styles.Information
    case "warning":
      return styles.Warning
    case "success":
      return styles.Success
    default:
      return ""
  }
}

export const Title: React.FC<{
  text: JSX.Element | string
  type?: string
  size?: "small" | "medium" | "large"
  style?: any
  doNotAnimateTitle?: boolean;
}> = (props): JSX.Element => {
  // Use memo to avoid re-rendering the title content when the text changes and animation
  const titleContent = React.useMemo(() => {
    if (typeof props.text !== "string") return props.text

    return (
      <span
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(props.text) }}
      />
    )
  }, [props.text])

  return (
    <h1
      style={props.style}
      className={`${styles.Title} ${style(props?.type || "")} ${
        props.size
          ? props.size === "small"
            ? styles.Small
            : styles.Medium
          : null
      }`}
    >
      {
        props.doNotAnimateTitle ? 
        titleContent : (
          <TextTransition className={styles.TitleText}>
            {titleContent}
          </TextTransition>
        )
      }
    </h1>
  )
}

export const Paragraph: React.FC<{ text: string | JSX.Element }> = (
  props
): JSX.Element => (
  <TextTransition
    style={{ display: "inline" }}
    springConfig={{ mass: 1, tension: 1000, friction: 100 }}
  >
  {props.text}
  </TextTransition>
)
export const Subtitle: React.FC<{ text: string | JSX.Element, style?: object | undefined }> = (
  props
): JSX.Element => {
  const subtitleContent = React.useMemo(() => {
    if (typeof props.text !== "string") return props.text

    return (
      <span
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(props.text, { USE_PROFILES: { html: true } }),
        }}
      />
    )
  }, [props.text])

  return (
    <Information
      className={styles.Subtitle}
      style={props.style}
      text={
        <TextTransition
          className={styles.TitleText}
          springConfig={{ mass: 1, tension: 1000, friction: 100 }}
        >
          {subtitleContent}
        </TextTransition>
      }
    />
  )
}

export const Important: React.FC<
  {
    text: string | JSX.Element
    shape?: boolean
    size?: "large"
  } & TitleProps
> = ({ size, shape, text, ...titleProps }): JSX.Element => (
  <h1
    {...titleProps}
    className={`${styles.Important} ${shape ? styles.ImportantShape : ""} ${
      size === "large" ? styles.Large : ""
    }`}
  >
    {text}
  </h1>
)

export const Information: React.FC<
  {
    text: string | JSX.Element
    shape?: boolean
    size?: "large"
  } & TitleProps
> = ({ size, text, shape, ...h1Props }): JSX.Element => (
  <span
    className={`${size === "large" ? styles.Large : null} ${
      styles.Information
    } ${shape ? styles.InformationShape : ""}`}
    {...h1Props}
  >
    {text}
  </span>
)
